export const seoConfig = {
  en: {
    title:
      'Nospen | Activities and offers between people. Buy, sell, gift, trade.',
    description:
      'Activities and offers between people. Buy, sell, gift, trade.',
    keywords:
      'trade, exchange, swap, barter, marketplace, books, clothes, electronics, furniture, vintage, collectibles, reuse, used, new, namams',
  },
  lt: {
    title:
      'Nospen | Veiklos ir pasiūlymai tarp žmonių. Pirk, Parduok, Mainyk, Dovanok',
    description:
      'Veiklos ir pasiūlymai tarp žmonių. Pirk, Parduok, Mainyk, Dovanok.',
    keywords:
      'mainai, keitimasis, platforma, knygos, drabužiai, elektronika, baldai, vintažas, kolekcionavimas, naudota, nauja, namams',
  },
};

export const getSeoConfig = (language: 'en' | 'lt' = 'en') => {
  return seoConfig[language] || seoConfig.en;
};

export const updatePageTitle = (title: string) => {
  document.title = title;
};

export const updateMetaTag = (name: string, content: string) => {
  const metaTag =
    document.querySelector(`meta[name="${name}"]`) ||
    document.querySelector(`meta[property="${name}"]`);

  if (metaTag) {
    metaTag.setAttribute('content', content);
  }
};

type SupportedLanguage = 'en' | 'lt';

const languageDomains: Record<SupportedLanguage, string> = {
  en: 'https://nospen.com',
  lt: 'https://nospen.lt',
};

const managedHreflangSelector =
  'link[rel="alternate"][data-seo-managed="true"]';

const ensureAlternateLink = (hrefLang: string, href: string) => {
  const selector = `${managedHreflangSelector}[hreflang="${hrefLang}"]`;
  let link = document.querySelector<HTMLLinkElement>(selector);

  if (!link) {
    link = document.createElement('link');
    link.setAttribute('rel', 'alternate');
    link.setAttribute('hreflang', hrefLang);
    link.setAttribute('data-seo-managed', 'true');
    document.head.appendChild(link);
  }

  link.setAttribute('href', href);
};

const updateAlternateHreflangLinks = () => {
  const { pathname, search } = window.location;
  const pathWithQuery = `${pathname}${search}`;

  ensureAlternateLink('en', `${languageDomains.en}${pathWithQuery}`);
  ensureAlternateLink('lt', `${languageDomains.lt}${pathWithQuery}`);
  ensureAlternateLink('x-default', `${languageDomains.en}${pathWithQuery}`);
};

const getCurrentLanguage = (): SupportedLanguage => {
  return document.documentElement.lang === 'lt' ? 'lt' : 'en';
};

const updateLocaleMeta = (language: SupportedLanguage) => {
  const primaryLocale = language === 'lt' ? 'lt_LT' : 'en_US';
  const alternateLocale = language === 'lt' ? 'en_US' : 'lt_LT';

  updateMetaTag('og:locale', primaryLocale);
  updateMetaTag('og:locale:alternate', alternateLocale);
};

export const updateCanonicalUrl = () => {
  const currentUrl = window.location.href;
  let canonicalLink = document.querySelector('link[rel="canonical"]');

  if (!canonicalLink) {
    canonicalLink = document.createElement('link');
    canonicalLink.setAttribute('rel', 'canonical');
    document.head.appendChild(canonicalLink);
  }

  canonicalLink.setAttribute('href', currentUrl);
};

export const updateDynamicUrls = () => {
  const currentUrl = window.location.href;
  const origin = window.location.origin;

  updateCanonicalUrl();
  updateMetaTag('og:url', currentUrl);
  updateMetaTag('twitter:url', currentUrl);

  const logoUrl = `${origin}/og-image.jpg`;
  updateMetaTag('og:image', logoUrl);
  updateMetaTag('twitter:image', logoUrl);

  updateAlternateHreflangLinks();
  updateLocaleMeta(getCurrentLanguage());
};

export const updatePageSeo = (language: 'en' | 'lt' = 'en') => {
  const config = getSeoConfig(language);

  updatePageTitle(config.title);
  updateMetaTag('description', config.description);
  updateMetaTag('keywords', config.keywords);
  updateMetaTag('og:title', config.title);
  updateMetaTag('og:description', config.description);
  updateMetaTag('twitter:title', config.title);
  updateMetaTag('twitter:description', config.description);

  document.documentElement.lang = language;

  updateLocaleMeta(language);

  updateDynamicUrls();
};

interface AssetSeoData {
  title: string;
  description?: string;
  imageUrl?: string;
  action?: string;
  category?: string;
}

export const updateAssetPageSeo = (assetData: AssetSeoData) => {
  const { title, description, imageUrl, action, category } = assetData;

  const fullTitle = `${title} | Nospen`;
  const fullDescription =
    description ||
    `${action || 'Dalyvis'} - ${category || 'prekė'} platformoje Nospen`;
  const fullImageUrl = imageUrl
    ? `${window.location.origin}${imageUrl}`
    : `${window.location.origin}/og-image.jpg`;

  document.title = fullTitle;
  updateMetaTag('description', fullDescription);

  updateMetaTag('og:title', fullTitle);
  updateMetaTag('og:description', fullDescription);
  updateMetaTag('og:image', fullImageUrl);
  updateMetaTag('og:url', window.location.href);

  updateMetaTag('twitter:title', fullTitle);
  updateMetaTag('twitter:description', fullDescription);
  updateMetaTag('twitter:image', fullImageUrl);

  updateCanonicalUrl();
  updateAlternateHreflangLinks();
  updateLocaleMeta(getCurrentLanguage());
};

interface UserSeoData {
  username: string;
  bio?: string;
  avatarUrl?: string;
}

export const updateUserPageSeo = (userData: UserSeoData) => {
  const { username, bio, avatarUrl } = userData;

  const fullTitle = `${username} | Nospen`;
  const fullDescription =
    bio || `Peržiūrėkite ${username} profilį platformoje Nospen`;
  const fullImageUrl = avatarUrl
    ? `${window.location.origin}${avatarUrl}`
    : `${window.location.origin}/og-image.jpg`;

  document.title = fullTitle;
  updateMetaTag('description', fullDescription);

  updateMetaTag('og:title', fullTitle);
  updateMetaTag('og:description', fullDescription);
  updateMetaTag('og:image', fullImageUrl);
  updateMetaTag('og:url', window.location.href);

  updateMetaTag('twitter:title', fullTitle);
  updateMetaTag('twitter:description', fullDescription);
  updateMetaTag('twitter:image', fullImageUrl);

  updateCanonicalUrl();
  updateAlternateHreflangLinks();
  updateLocaleMeta(getCurrentLanguage());
};

export const resetToDefaultSeo = (language: 'en' | 'lt' = 'lt') => {
  updatePageSeo(language);
};
